import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getStripeEnvironment } from "@/lib/stripe";
import { tierForPriceId } from "@/lib/pricing";

export interface SubscriptionRow {
  id: string;
  user_id: string;
  stripe_subscription_id: string;
  stripe_customer_id: string;
  product_id: string;
  price_id: string;
  status: string;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  environment: string;
  certification_id: string | null;
  created_at: string;
  updated_at: string;
}

/** Per-certification view of a subscription row, used by Settings billing. */
export interface CertificationSubscription {
  certificationId: string | null;
  row: SubscriptionRow;
  tier: "monthly" | "quarterly" | "yearly" | null;
  isActive: boolean;
  renewsAt: Date | null;
  cancelsAtPeriodEnd: boolean;
  pastDue: boolean;
  /** Change plan is only offered while Stripe reports a plain `active` sub. */
  canChangePlan: boolean;
}

const ACTIVE_STATUSES = new Set(["active", "trialing", "past_due"]);

function deriveRow(row: SubscriptionRow) {
  const renewsAt = row.current_period_end ? new Date(row.current_period_end) : null;
  const inPeriod = !renewsAt || renewsAt > new Date();
  const isActive =
    (ACTIVE_STATUSES.has(row.status) && inPeriod) ||
    (row.status === "canceled" && !!renewsAt && renewsAt > new Date());
  const canceledWithFutureAccess = row.status === "canceled" && !!renewsAt && renewsAt > new Date();
  const cancelsAtPeriodEnd = !!row.cancel_at_period_end || canceledWithFutureAccess;
  const pastDue = row.status === "past_due";
  return {
    tier: tierForPriceId[row.price_id] ?? null,
    isActive,
    renewsAt,
    cancelsAtPeriodEnd,
    pastDue,
    canChangePlan: row.status === "active" && inPeriod && !cancelsAtPeriodEnd,
  };
}

export function useSubscription() {
  const { user } = useAuth();
  const [rows, setRows] = useState<SubscriptionRow[]>([]);
  const [loading, setLoading] = useState(true);

  const env = (() => {
    try { return getStripeEnvironment(); } catch { return "sandbox" as const; }
  })();

  const refetch = useCallback(async () => {
    if (!user) { setRows([]); setLoading(false); return; }
    setLoading(true);
    const { data } = await supabase
      .from("subscriptions")
      .select("*")
      .eq("user_id", user.id)
      .eq("environment", env)
      .order("created_at", { ascending: false });
    setRows((data as SubscriptionRow[] | null) ?? []);
    setLoading(false);
  }, [user, env]);

  useEffect(() => { void refetch(); }, [refetch]);

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`subscriptions:${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "subscriptions", filter: `user_id=eq.${user.id}` },
        () => { void refetch(); },
      )
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [user, refetch]);

  // Newest row overall — kept for existing single-subscription callers.
  const subscription = rows[0] ?? null;
  const derived = subscription ? deriveRow(subscription) : null;

  // One entry per certification, using the newest row for that certification.
  const byCertification: CertificationSubscription[] = (() => {
    const seen = new Set<string>();
    const out: CertificationSubscription[] = [];
    for (const row of rows) {
      const key = row.certification_id ?? "__none__";
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ certificationId: row.certification_id ?? null, row, ...deriveRow(row) });
    }
    return out;
  })();

  return {
    subscription,
    subscriptions: rows,
    byCertification,
    tier: derived?.tier ?? null,
    isActive: derived?.isActive ?? false,
    renewsAt: derived?.renewsAt ?? null,
    cancelsAtPeriodEnd: derived?.cancelsAtPeriodEnd ?? false,
    pastDue: derived?.pastDue ?? false,
    loading,
    refetch,
  };
}
